import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useTechnologies } from '../hooks/useAPI';

const TechStackSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const { data: technologies, isLoading, error } = useTechnologies({ active: 'true' });

  // Debug: Log what we receive from API
  React.useEffect(() => {
    console.log('Technologies from API:', technologies);
    console.log('Is Loading:', isLoading);
    console.log('Error:', error);
  }, [technologies, isLoading, error]);

  // Default technologies if API fails
  const defaultTechnologies = [
    {
      id: 1,
      name: 'React',
      category: 'Frontend',
      logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg'
    },
    {
      id: 2,
      name: 'Node.js',
      category: 'Backend',
      logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg'
    },
    {
      id: 3,
      name: 'Python',
      category: 'AI/ML',
      logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg'
    },
    {
      id: 4,
      name: 'MongoDB',
      category: 'Database',
      logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mongodb/mongodb-original.svg'
    },
    {
      id: 5,
      name: 'Docker',
      category: 'DevOps',
      logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/docker/docker-original.svg'
    },
    {
      id: 6,
      name: 'AWS',
      category: 'Cloud',
      logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/amazonwebservices/amazonwebservices-original.svg'
    },
    {
      id: 7,
      name: 'TypeScript',
      category: 'Language',
      logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/typescript/typescript-original.svg'
    },
    {
      id: 8,
      name: 'GraphQL',
      category: 'API',
      logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/graphql/graphql-plain.svg'
    },
    {
      id: 9,
      name: 'Kubernetes',
      category: 'DevOps',
      logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/kubernetes/kubernetes-plain.svg'
    },
    {
      id: 10,
      name: 'TensorFlow',
      category: 'AI/ML',
      logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/tensorflow/tensorflow-original.svg'
    },
    {
      id: 11,
      name: 'Vue.js',
      category: 'Frontend',
      logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/vuejs/vuejs-original.svg'
    },
    {
      id: 12,
      name: 'PostgreSQL',
      category: 'Database',
      logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/postgresql/postgresql-original.svg'
    }
  ];

  const displayTechnologies = technologies && technologies.length > 0 ? technologies : defaultTechnologies;

  const categories = [...new Set(displayTechnologies.map(tech => tech.category))];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' }
    }
  };

  const techVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.5, ease: 'easeOut' }
    }
  };

  return (
    <section id="tech-stack" className="py-20 md:py-24 bg-lion-gray">
      <div className="container mx-auto px-6" ref={ref}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="text-center mb-16"
        >
          <motion.span 
            variants={itemVariants}
            className="font-bold text-lion-yellow text-sm uppercase tracking-wider"
          >
            OUR TECHNOLOGY STACK
          </motion.span>
          
          <motion.h2 
            variants={itemVariants}
            className="text-3xl md:text-4xl font-poppins font-bold text-lion-blue mt-2 mb-4"
          >
            Cutting-Edge Technologies
          </motion.h2>
          
          <motion.p 
            variants={itemVariants}
            className="max-w-2xl mx-auto text-gray-600"
          >
            We leverage the latest and most powerful technologies to build robust, scalable, and innovative solutions.
          </motion.p>
        </motion.div>

        {isLoading ? (
          <div className="flex justify-center">
            <div className="spinner w-8 h-8"></div>
          </div>
        ) : (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            className="space-y-12"
          >
            {categories.map((category, categoryIndex) => {
              const categoryTechs = displayTechnologies.filter(tech => tech.category === category);
              
              return (
                <motion.div
                  key={category}
                  variants={itemVariants}
                  className="text-center"
                >
                  <h3 className="text-xl font-poppins font-semibold text-lion-blue mb-8">
                    {category}
                  </h3>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6">
                    {categoryTechs.map((tech, index) => (
                      <motion.div
                        key={tech.id || `${category}-${index}`}
                        variants={techVariants}
                        custom={index}
                        className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transform hover:-translate-y-2 transition-all duration-300 group"
                      >
                        <div className="flex flex-col items-center">
                          {tech.logo ? (
                            <img
                              src={tech.logo}
                              alt={tech.name}
                              className="w-12 h-12 mb-3 group-hover:scale-110 transition-transform duration-300"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          
                          {/* Fallback icon */}
                          <div 
                            className="w-12 h-12 mb-3 bg-gradient-to-br from-lion-blue to-lion-yellow rounded-lg flex items-center justify-center text-white font-bold text-lg group-hover:scale-110 transition-transform duration-300"
                            style={{ display: tech.logo ? 'none' : 'flex' }}
                          >
                            {tech.name.charAt(0)}
                          </div>
                          
                          <h4 className="text-sm font-medium text-lion-blue group-hover:text-lion-yellow transition-colors duration-300">
                            {tech.name}
                          </h4>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        )}

        {error && (
          <motion.p 
            variants={itemVariants}
            className="text-gray-500 text-center mt-8"
          >
            Technologies will be loaded shortly. Please check back soon.
          </motion.p>
        )}

        {/* Additional Info */}
        <motion.div
          variants={itemVariants}
          className="text-center mt-16 bg-white p-8 rounded-lg"
        >
          <h3 className="text-2xl font-poppins font-bold text-lion-blue mb-4">
            Always Evolving
          </h3>
          <p className="text-gray-600 max-w-3xl mx-auto">
            Our technology stack is constantly evolving. We stay ahead of the curve by adopting emerging technologies 
            and best practices to ensure our solutions are future-proof and deliver maximum value to our clients.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default TechStackSection;